import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { cn } from "../../../design-system/index.js";
import { Button, Card } from "../../../design-system/index.js";
import {
  ArrowLeft,
  Eye,
  EyeOff,
  Users,
  HeartHandshake,
  BadgeCheck,
  ArrowLeftRight,
} from "lucide-react";
import { supabase } from "../../../supabaseClient.jsx";
import { buildAuthRedirect } from "../../../utils/authRedirect.js";
import { useAuth } from "../../../app/AuthContext.jsx";
import { localTypeToDb } from "../../../utils/userTypes.js";

const VALID_TYPES = new Set(["family", "babysitter"]);

const PROFILE_CONTENT = {
  family: {
    label: "família",
    bannerTitle: "Você está criando uma conta de Família",
    bannerHint: "Vai contratar babás para cuidar dos seus tesourinhos.",
    subtitle:
      "Crie sua conta para encontrar profissionais com segurança e gerenciar agendamentos.",
    icon: Users,
    accent: "var(--color-rose-400)",
    bannerClass:
      "bg-[var(--color-rose-400)/10] border-[var(--color-rose-400)/40]",
    other: { label: "Babá", href: "/profile-selection?type=babysitter" },
  },
  babysitter: {
    label: "babá",
    bannerTitle: "Você está criando uma conta de Babá",
    bannerHint: "Vai oferecer seus serviços e ser encontrada por famílias.",
    subtitle:
      "Crie sua conta para divulgar seu perfil profissional e receber oportunidades.",
    icon: HeartHandshake,
    accent: "var(--color-slate-blue-400)",
    bannerClass:
      "bg-[var(--color-slate-blue-400)/10] border-[var(--color-slate-blue-400)/40]",
    other: { label: "Família", href: "/profile-selection?type=family" },
  },
};

export default function ProfileSelectionPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUserType } = useAuth();
  const rawType = (searchParams.get("type") || "family").toLowerCase();
  const type = VALID_TYPES.has(rawType) ? rawType : "family";
  const errorRef = useRef(null);
  const oauthHandledRef = useRef(false);

  const [nome, setNome] = useState("");
  const [sobrenome, setSobrenome] = useState("");
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [showSenha, setShowSenha] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.focus();
    }
  }, [error]);

  const splitName = (fullName) => {
    const cleaned = String(fullName || "").trim();
    if (!cleaned) {
      return { nome: "Novo", sobrenome: "Usuário" };
    }

    const parts = cleaned.split(/\s+/);
    if (parts.length === 1) {
      return { nome: parts[0], sobrenome: "Usuário" };
    }

    return {
      nome: parts[0],
      sobrenome: parts.slice(1).join(" "),
    };
  };

  const persistOAuthUserType = async (sessionUser) => {
    const dbType = localTypeToDb(type);
    const email = sessionUser?.email
      ? String(sessionUser.email).trim().toLowerCase()
      : null;

    const { data: existingUser, error: existingUserError } = await supabase
      .from("usuarios")
      .select("id")
      .eq("id", sessionUser.id)
      .maybeSingle();

    if (existingUserError) {
      throw existingUserError;
    }

    if (existingUser) {
      const updatePayload = { tipo: dbType };
      if (email) {
        updatePayload.email = email;
      }

      const { error: updateError } = await supabase
        .from("usuarios")
        .update(updatePayload)
        .eq("id", sessionUser.id);

      if (updateError) {
        throw updateError;
      }
      return;
    }

    const metadata = sessionUser?.user_metadata || {};
    const metadataName =
      metadata?.name ||
      [metadata?.given_name, metadata?.family_name].filter(Boolean).join(" ");
    const { nome, sobrenome } = splitName(metadataName);

    const { error: insertError } = await supabase.from("usuarios").insert([
      {
        id: sessionUser.id,
        email,
        tipo: dbType,
        nome,
        sobrenome,
      },
    ]);

    if (insertError) {
      throw insertError;
    }
  };

  // Após retorno do OAuth Google, redirecionar conforme tipo
  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event !== "SIGNED_IN" || !session || oauthHandledRef.current) {
        return;
      }

      oauthHandledRef.current = true;

      try {
        await persistOAuthUserType(session.user);
        setUserType(type);

        const redirectAfterOAuth =
          sessionStorage.getItem("oauth_redirect") || getRedirectPath();
        sessionStorage.removeItem("oauth_redirect");
        navigate(redirectAfterOAuth, { replace: true });
      } catch (persistError) {
        console.error("Erro ao persistir tipo após OAuth:", persistError);
        setGoogleLoading(false);
        setError(
          "Login concluído, mas não foi possível salvar seu tipo de perfil. Tente novamente.",
        );
        oauthHandledRef.current = false;
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate, type]);

  const getRedirectPath = () => {
    if (type === "babysitter") return "/cadastro-baba";
    return "/cadastro-familia";
  };

  const handleGoogle = async () => {
    setError(null);
    setGoogleLoading(true);
    // Salva para onde redirecionar após o OAuth
    sessionStorage.setItem("oauth_redirect", getRedirectPath());
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: buildAuthRedirect(`/profile-selection?type=${type}`),
      },
    });
    if (error) {
      setError("Falha ao conectar com Google. Tente novamente.");
      sessionStorage.removeItem("oauth_redirect");
      setGoogleLoading(false);
    }
    // Se bem-sucedido, o navegador será redirecionado pelo Supabase
  };

  const handleEmailSignUp = async (e) => {
    e.preventDefault();
    setError(null);

    if (!nome.trim() || !sobrenome.trim()) {
      setError("Preencha nome e sobrenome.");
      return;
    }
    if (!email.trim()) {
      setError("Informe seu e-mail.");
      return;
    }
    if (senha.length < 6) {
      setError("A senha deve ter no mínimo 6 caracteres.");
      return;
    }

    setLoading(true);
    const { error } = await supabase.auth.signUp({
      email: email.trim(),
      password: senha,
      options: {
        data: { nome: nome.trim(), sobrenome: sobrenome.trim(), tipo: type },
      },
    });
    setLoading(false);

    if (error) {
      setError(
        error.message === "User already registered"
          ? "Este e-mail já está cadastrado. Tente fazer login."
          : error.message,
      );
    } else {
      setSuccess(
        "Cadastro realizado! Verifique seu e-mail para confirmar a conta.",
      );
    }
  };

  const content = PROFILE_CONTENT[type];
  const typeSubtitle = content.subtitle;
  const TypeIcon = content.icon;

  return (
    <main
      className="min-h-screen flex items-center justify-center py-16"
      aria-labelledby="profile-selection-title"
    >
      <div className="w-full max-w-2xl px-4 sm:px-0">
        <div className="text-center mb-6">
          <h1
            className={cn(
              "font-heading text-3xl font-bold",
              "text-warm-900 mb-2",
            )}
            id="profile-selection-title"
          >
            Complete seu cadastro
          </h1>
          <p className="text-sm text-warm-600">{typeSubtitle}</p>
        </div>

        {/* Banner de tipo — sinaliza de forma inconfundível qual formulário é este */}
        <div
          className={cn(
            "mb-6 overflow-hidden rounded-2xl border",
            content.bannerClass,
          )}
          role="status"
        >
          {/* Zona 1 — o que você está criando */}
          <div className="flex items-center gap-4 px-5 py-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white shadow-sm">
              <TypeIcon
                size={24}
                style={{ color: content.accent }}
                strokeWidth={1.9}
              />
            </span>
            <div className="min-w-0 flex-1 text-left">
              <span
                className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide"
                style={{ color: content.accent }}
              >
                <BadgeCheck size={13} />
                Cadastro selecionado
              </span>
              <p className="font-bold text-warm-900 leading-tight">
                {content.bannerTitle}
              </p>
              <p className="text-sm text-warm-600">{content.bannerHint}</p>
            </div>
          </div>

          {/* Zona 2 — trocar de tipo (ação explícita) */}
          <div className="flex flex-col gap-2 border-t border-white/60 bg-white/50 px-5 py-3 sm:flex-row sm:items-center sm:justify-between">
            <span className="text-sm text-warm-600">
              Não é o que você procura?
            </span>
            <Link
              to={content.other.href}
              className="inline-flex items-center justify-center gap-2 rounded-full border bg-white px-4 py-1.5 text-sm font-semibold shadow-sm transition-colors hover:bg-warm-50"
              style={{ color: content.accent, borderColor: content.accent }}
            >
              <ArrowLeftRight size={14} />
              Trocar para {content.other.label}
            </Link>
          </div>
        </div>

        <Card
          className="p-8"
          role="region"
          aria-labelledby="profile-selection-title"
        >
          <div className="space-y-6">
            {/* Google */}
            <div className="text-center">
              <h3 className="text-lg font-semibold text-warm-800 mb-2">
                Cadastro rápido com Google
              </h3>
              <p className="text-sm text-warm-500 mb-4">
                Use sua conta do Google para um cadastro mais rápido e seguro
              </p>
              <Button
                variant="outline"
                className="w-full"
                onClick={handleGoogle}
                disabled={googleLoading}
                aria-busy={googleLoading}
                aria-label={`Continuar com Google para perfil ${content.label}`}
              >
                {googleLoading ? (
                  <span className="flex items-center gap-2">
                    <svg
                      className="animate-spin w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v8z"
                      />
                    </svg>
                    Redirecionando...
                  </span>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                      <path
                        fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    Continuar com Google
                  </>
                )}
              </Button>
            </div>

            {/* Divisor */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-warm-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-warm-500">ou</span>
              </div>
            </div>

            {/* Formulário e-mail */}
            <div>
              <h3 className="text-lg font-semibold text-warm-800 mb-4">
                Cadastro com e-mail
              </h3>

              {error && (
                <div
                  ref={errorRef}
                  tabIndex={-1}
                  role="alert"
                  aria-live="assertive"
                  className="mb-4 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-3 focus:outline-none focus:ring-2 focus:ring-red-200"
                >
                  {error}
                </div>
              )}
              {success && (
                <div
                  role="status"
                  aria-live="polite"
                  className="mb-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm px-4 py-3"
                >
                  {success}
                </div>
              )}

              <form
                className="space-y-4"
                onSubmit={handleEmailSignUp}
                noValidate
              >
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="profile-selection-name"
                      className="block text-sm font-medium text-warm-700 mb-1"
                    >
                      Nome
                    </label>
                    <input
                      id="profile-selection-name"
                      type="text"
                      value={nome}
                      onChange={(e) => setNome(e.target.value)}
                      required
                      autoComplete="given-name"
                      className={cn(
                        "w-full rounded-xl border border-warm-200",
                        "bg-white px-4 py-2.5 text-sm",
                        "focus:outline-none focus:ring-2 focus:ring-rose-400/40",
                        "focus:border-rose-400 transition",
                      )}
                      placeholder="Seu nome"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="profile-selection-last-name"
                      className="block text-sm font-medium text-warm-700 mb-1"
                    >
                      Sobrenome
                    </label>
                    <input
                      id="profile-selection-last-name"
                      type="text"
                      value={sobrenome}
                      onChange={(e) => setSobrenome(e.target.value)}
                      required
                      autoComplete="family-name"
                      className={cn(
                        "w-full rounded-xl border border-warm-200",
                        "bg-white px-4 py-2.5 text-sm",
                        "focus:outline-none focus:ring-2 focus:ring-rose-400/40",
                        "focus:border-rose-400 transition",
                      )}
                      placeholder="Seu sobrenome"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="profile-selection-email"
                    className="block text-sm font-medium text-warm-700 mb-1"
                  >
                    E-mail
                  </label>
                  <input
                    id="profile-selection-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    className={cn(
                      "w-full rounded-xl border border-warm-200",
                      "bg-white px-4 py-2.5 text-sm",
                      "focus:outline-none focus:ring-2 focus:ring-rose-400/40",
                      "focus:border-rose-400 transition",
                    )}
                    placeholder="seu@email.com"
                  />
                </div>

                <div>
                  <label
                    htmlFor="profile-selection-password"
                    className="block text-sm font-medium text-warm-700 mb-1"
                  >
                    Senha
                  </label>
                  <div className="relative">
                    <input
                      id="profile-selection-password"
                      type={showSenha ? "text" : "password"}
                      value={senha}
                      onChange={(e) => setSenha(e.target.value)}
                      required
                      autoComplete="new-password"
                      aria-describedby="profile-selection-password-hint"
                      className={cn(
                        "w-full rounded-xl border border-warm-200",
                        "bg-white px-4 py-2.5 pr-10 text-sm",
                        "focus:outline-none focus:ring-2 focus:ring-rose-400/40",
                        "focus:border-rose-400 transition",
                      )}
                      placeholder="Mínimo 6 caracteres"
                    />
                    <button
                      type="button"
                      onClick={() => setShowSenha((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-warm-400 hover:text-warm-600"
                      aria-label={showSenha ? "Ocultar senha" : "Mostrar senha"}
                      aria-pressed={showSenha}
                    >
                      {showSenha ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  <p
                    id="profile-selection-password-hint"
                    className="mt-1 text-xs text-warm-500"
                  >
                    Use no mínimo 6 caracteres.
                  </p>
                </div>

                <Button
                  type="submit"
                  variant="default"
                  className="w-full"
                  disabled={loading || !!success}
                >
                  {loading ? "Criando conta..." : "Criar conta"}
                </Button>
              </form>
            </div>
          </div>

          <div className="mb-4 mt-8 flex justify-center items-center w-full">
            <Button variant="ghost" size="sm" asChild>
              <Link
                to="/"
                aria-label="Voltar para a página principal"
                className="inline-flex items-center gap-2"
              >
                <ArrowLeft size={14} />
                Voltar para a home
              </Link>
            </Button>
          </div>
        </Card>
      </div>
    </main>
  );
}
